/**
 * Copyright (c) Nicolas Gallagher.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * @noflow
 */

import getBoundingClientRect from './getBoundingClientRect';

const getRect = (node: Element) => {
  // Unlike the DOM's getBoundingClientRect, React Native layout measurements
  // for "height" and "width" ignore scale transforms.
  // https://developer.mozilla.org/en-US/docs/Web/API/CSS_Object_Model/Determining_the_dimensions_of_elements
  const { x, y, top, left } = getBoundingClientRect(node) as DOMRect;
  const width = (node as HTMLElement).offsetWidth;
  const height = (node as HTMLElement).offsetHeight;
  return { x, y, width, height, top, left };
};

type TMeasureCallback = (
  x: number,
  y: number,
  width: number,
  height: number,
  left: number,
  top: number,
) => void;

type TMeasureInWindowCallback = (x: number, y: number, width: number, height: number) => void;

const measureLayout = (
  node: Element,
  relativeToNativeNode: HTMLElement | null,
  callback: TMeasureCallback,
) => {
  const relativeNode = relativeToNativeNode || (node && node.parentNode);

  if (node && relativeNode) {
    setTimeout(() => {
      const relativeRect = getBoundingClientRect(relativeNode as HTMLElement) as DOMRect;
      const { height, left, top, width } = getRect(node);
      const x = left - relativeRect.left;
      const y = top - relativeRect.top;
      callback(x, y, width, height, left, top);
    }, 0);
  }
};

const elementsToIgnore = {
  A: true,
  BODY: true,
  INPUT: true,
  SELECT: true,
  TEXTAREA: true,
} as const;

const UIManager = {
  blur(node: HTMLElement) {
    try {
      node.blur();
    } catch (err) {}
  },

  focus(node: HTMLElement) {
    try {
      const name = node.nodeName as keyof typeof elementsToIgnore;
      // A tabIndex of -1 allows element to be programmatically focused but
      // prevents keyboard focus. We don't want to set the tabindex value on
      // elements that should not prevent keyboard focus.
      if (
        node.getAttribute('tabIndex') == null &&
        node.isContentEditable !== true &&
        elementsToIgnore[name] == null
      ) {
        node.setAttribute('tabIndex', '-1');
      }
      node.focus();
    } catch (err) {}
  },

  measure(node: Element, callback: TMeasureCallback) {
    measureLayout(node, null, callback);
  },

  measureInWindow(node: HTMLElement, callback: TMeasureInWindowCallback) {
    if (node) {
      setTimeout(() => {
        const { height, left, top, width } = getRect(node);
        callback(left, top, width, height);
      }, 0);
    }
  },

  measureLayout(
    node: HTMLElement,
    relativeToNativeNode: HTMLElement | null,
    onFail: undefined,
    onSuccess: TMeasureCallback,
  ) {
    measureLayout(node, relativeToNativeNode, onSuccess);
  },
};

export default UIManager;
